import * as React from 'react';

import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
} from './components/ui/field.js';
import { DimeInfoHint } from './DimeInfoHint.js';
import { FieldRequiredContext } from './lib/field-required-context.js';

export function DimeFieldSet({
  children,
  ...props
}: Readonly<React.ComponentProps<typeof FieldSet>>) {
  if (import.meta.env.DEV) {
    const hasAccessibleName =
      props['aria-label'] != null ||
      props['aria-labelledby'] != null ||
      React.Children.toArray(children).some(
        (child) => React.isValidElement(child) && child.type === DimeFieldLegend,
      );
    if (!hasAccessibleName) {
      // eslint-disable-next-line no-console -- intentional dev-only warning, not a stray debug log
      console.error(
        'DimeFieldSet rendered without an accessible name — add a DimeFieldLegend child or an aria-label so assistive technology can identify this group.',
      );
    }
  }
  return <FieldSet {...props}>{children}</FieldSet>;
}

export function DimeFieldLegend({ ...props }: Readonly<React.ComponentProps<typeof FieldLegend>>) {
  return <FieldLegend {...props} />;
}

export function DimeFieldGroup({ ...props }: Readonly<React.ComponentProps<typeof FieldGroup>>) {
  return <FieldGroup {...props} />;
}

export function DimeField({
  required,
  ...props
}: Readonly<React.ComponentProps<typeof Field> & { required?: boolean }>) {
  return (
    <FieldRequiredContext.Provider value={required ?? false}>
      <Field {...props} />
    </FieldRequiredContext.Provider>
  );
}

export function DimeFieldLabel({
  children,
  hint,
  ...props
}: Readonly<React.ComponentProps<typeof FieldLabel> & { hint?: string }>) {
  const required = React.useContext(FieldRequiredContext);
  const label = (
    <FieldLabel {...props}>
      {children}
      {required && (
        // WHY: no margin -- FieldLabel's own flex gap-2 is the single source of this spacing.
        <span className="text-destructive" aria-hidden="true">
          *
        </span>
      )}
    </FieldLabel>
  );
  if (!hint) return label;
  return (
    <div className="flex items-center gap-1">
      {label}
      <DimeInfoHint content={hint} />
    </div>
  );
}

export function DimeFieldDescription({
  ...props
}: Readonly<React.ComponentProps<typeof FieldDescription>>) {
  return <FieldDescription {...props} />;
}

export function DimeFieldError({ ...props }: Readonly<React.ComponentProps<typeof FieldError>>) {
  return <FieldError {...props} />;
}

export function DimeFieldContent({
  ...props
}: Readonly<React.ComponentProps<typeof FieldContent>>) {
  return <FieldContent {...props} />;
}

export function DimeFieldTitle({ ...props }: Readonly<React.ComponentProps<typeof FieldTitle>>) {
  return <FieldTitle {...props} />;
}

export function DimeFieldSeparator({
  ...props
}: Readonly<React.ComponentProps<typeof FieldSeparator>>) {
  return <FieldSeparator {...props} />;
}
